/**
 * Where am I (owner, 2026-10-12: "я замахався клікати всі підряд вкладки, щоб зрозуміти де я і що я роблю,
 * поки знаходиш потрібне — забуваєш що шукав"). The menu has five sections; a section's pages sit behind
 * the tabs on its top, and a page deeper than that (a direction, an account) says its way back in a line
 * of crumbs. The sections and their tabs are named here, once, so the menu and the tabs never disagree.
 */

import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import { useT } from '../../i18n'
import { useApp } from '../../lib/context'

export type SectionId = 'overview' | 'phones' | 'directions' | 'posting' | 'settings'

interface Tab { to: string; label: string; adminOnly?: boolean; personOnly?: boolean }

export function useSections(): Record<SectionId, Tab[]> {
  const t = useT()
  const s = t.sections
  return {
    overview: [],
    phones: [],
    directions: [
      { to: '/directions', label: s.directions },
      { to: '/accounts', label: s.accounts },
      { to: '/publications', label: s.publications },
      { to: '/links', label: s.links },
    ],
    posting: [
      { to: '/posts', label: s.posts },
      { to: '/distributions', label: s.distributions },
      { to: '/videos', label: s.videos },
      //: what posts are made of (owner, 2026-10-12: "Соурсес теж треба у розділ постінг")
      { to: '/sources', label: s.sources },
    ],
    settings: [
      { to: '/machines', label: s.machines },
      { to: '/scenarios', label: s.scenarios },
      { to: '/users', label: s.users, adminOnly: true },
      { to: '/keys', label: s.keys, adminOnly: true },
      { to: '/me/keys', label: s.myClaude, personOnly: true },
    ],
  }
}

/** Which section a path belongs to — the menu lights the section, whichever of its pages is open. */
export function sectionOf(path: string): SectionId {
  const first = '/' + (path.split('/')[1] ?? '')
  if (first === '/') return 'overview'
  if (first === '/phones') return 'phones'
  if (['/directions', '/accounts', '/publications', '/links'].includes(first)) return 'directions'
  if (['/posts', '/distributions', '/videos', '/sources', '/posting'].includes(first)) return 'posting'
  return 'settings'
}

/** The tabs on top of a section's page. */
export function SectionTabs({ section }: { section: SectionId }) {
  const tabs = useSections()[section]
  const { auth } = useApp()
  const shown = tabs.filter((x) => (!x.adminOnly || auth.isAdmin) && (!x.personOnly || !!auth.user))
  if (shown.length < 2) return null
  return (
    <nav className="section-tabs" aria-label="section">
      {shown.map((x) => (
        <NavLink key={x.to} to={x.to} end={x.to === '/directions'}>{x.label}</NavLink>
      ))}
    </nav>
  )
}

/** A section's page: its tabs, then the page. */
export function Section({ section, children }: { section: SectionId; children: ReactNode }) {
  return (
    <div className="page col">
      <SectionTabs section={section} />
      {children}
    </div>
  )
}

/** "Напрями › Ігри › therealmugik@gmail.com" — every step but the last one leads back. */
export function Crumbs({ items }: { items: { label: ReactNode; to?: string }[] }) {
  return (
    <nav className="crumbs" aria-label="breadcrumbs">
      {items.map((x, i) => (
        <span key={i} className="crumb">
          {i > 0 && <ChevronRight className="ico sep" size={14} aria-hidden />}
          {x.to && i < items.length - 1 ? <Link to={x.to}>{x.label}</Link> : <span aria-current={i === items.length - 1 ? 'page' : undefined}>{x.label}</span>}
        </span>
      ))}
    </nav>
  )
}

/** The menu's light: on for the section the open page belongs to. */
export function useSection(): SectionId {
  return sectionOf(useLocation().pathname)
}
